import { atom } from 'jotai';
import type { ChatMessage } from '../components/chat/messageUtils';
import { isSealed, openText, sealText } from './e2ee/rest';

// Drafts (M62, docs/chat-round-four.md D228): what you typed in a chat, kept
// on this device per account and chat until you send it or sign out. Never
// sent to the server. Kept sealed with this device's key (D281, #84): read
// once into memory (openDrafts), then every change is sealed and written in
// the background. Where no key can be kept, drafts last only while the page
// is open; they're never written in plain text.

export interface Draft {
  text: string;
  /**
   * The message it replies to, by id only: the quote is looked up again when
   * the chat opens, so a deleted message's words are never kept here.
   */
  replyToId?: string;
  /** The chat's family, so drafts go when the chat or family does. */
  familyId?: string;
  /** When it was last changed (ms), for dropping the oldest. */
  at: number;
}

export type Drafts = Record<string, Draft>;

export const MAX_DRAFTS = 50;
const KEY = 'twinden:drafts:';

/** The signed-in person's drafts, by chat id, for the chat list. */
export const draftsAtom = atom<Drafts>({});

function isDraft(value: unknown): value is Draft {
  const draft = value as Draft | null;
  return (
    !!draft &&
    typeof draft.text === 'string' &&
    typeof draft.at === 'number' &&
    (draft.replyToId === undefined || typeof draft.replyToId === 'string') &&
    (draft.familyId === undefined || typeof draft.familyId === 'string')
  );
}

function parseDrafts(raw: string | null): Drafts {
  try {
    const parsed = raw ? (JSON.parse(raw) as unknown) : {};
    if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) {
      return {};
    }
    return Object.fromEntries(
      Object.entries(parsed).filter(([, draft]) => isDraft(draft))
    ) as Drafts;
  } catch {
    return {};
  }
}

// This page's copy of each person's drafts; undefined until opened.
const inMemory = new Map<string, Drafts>();
const opening = new Map<string, Promise<Drafts>>();
// Writes go out one after another, so the last change is what's kept.
const writing = new Map<string, Promise<void>>();

function readStored(userId: string): string | null {
  try {
    return localStorage.getItem(KEY + userId);
  } catch {
    return null;
  }
}

/**
 * Opens this person's drafts from the device (D281). Changes made before it
 * finished are kept over what was stored; plain drafts from before sealing
 * are read once and written back sealed.
 */
export function openDrafts(userId: string): Promise<Drafts> {
  let open = opening.get(userId);
  if (!open) {
    open = (async () => {
      const raw = readStored(userId);
      const stored = !raw
        ? {}
        : isSealed(raw)
          ? parseDrafts(await openText(userId, raw))
          : parseDrafts(raw);
      const merged = { ...stored, ...(inMemory.get(userId) ?? {}) };
      inMemory.set(userId, merged);
      // Old plain drafts are sealed now (or dropped where nothing can be kept).
      if (raw && !isSealed(raw)) persist(userId);
      return { ...merged };
    })();
    opening.set(userId, open);
  }
  return open;
}

/** This person's drafts as this page has them; empty until opened. */
export function loadDrafts(userId: string): Drafts {
  return { ...(inMemory.get(userId) ?? {}) };
}

function persist(userId: string) {
  const previous = writing.get(userId) ?? Promise.resolve();
  const next = previous
    .then(async () => {
      // Never over what's stored before it has been read.
      await openDrafts(userId);
      const drafts = inMemory.get(userId) ?? {};
      if (Object.keys(drafts).length === 0) {
        localStorage.removeItem(KEY + userId);
        return;
      }
      const sealed = await sealText(userId, JSON.stringify(drafts));
      if (sealed) localStorage.setItem(KEY + userId, sealed);
      else localStorage.removeItem(KEY + userId);
    })
    .catch(() => {
      // Private mode or full storage: the draft just isn't kept.
    });
  writing.set(userId, next);
}

/** Forget this page's copy of everyone's drafts (tests: a fresh page). */
export function resetDraftMemory() {
  inMemory.clear();
  opening.clear();
  writing.clear();
}

/** Everything written so far has reached storage (tests, signing out). */
export function draftsWritten(userId: string): Promise<void> {
  return writing.get(userId) ?? Promise.resolve();
}

function write(userId: string, drafts: Drafts) {
  inMemory.set(userId, { ...drafts });
  persist(userId);
}

/**
 * Keeps (or, with blank text, drops) one chat's draft and returns them all.
 * A reply with nothing typed isn't kept. At most MAX_DRAFTS, oldest out.
 */
export function saveDraft(
  userId: string,
  conversationId: string,
  draft: { text: string; replyToId?: string | null; familyId?: string },
  now = Date.now()
): Drafts {
  const drafts = loadDrafts(userId);
  if (!draft.text.trim()) {
    if (!(conversationId in drafts)) return drafts;
    delete drafts[conversationId];
    write(userId, drafts);
    return drafts;
  }
  const previous = drafts[conversationId];
  const familyId = draft.familyId ?? previous?.familyId;
  const next: Draft = {
    text: draft.text,
    ...(draft.replyToId ? { replyToId: draft.replyToId } : {}),
    ...(familyId ? { familyId } : {}),
    at: now,
  };
  if (
    previous &&
    previous.text === next.text &&
    previous.replyToId === next.replyToId &&
    previous.familyId === next.familyId
  ) {
    return drafts;
  }
  drafts[conversationId] = next;
  const ids = Object.keys(drafts);
  if (ids.length > MAX_DRAFTS) {
    ids
      .sort((a, b) => drafts[a]!.at - drafts[b]!.at)
      .slice(0, ids.length - MAX_DRAFTS)
      .forEach((id) => delete drafts[id]);
  }
  write(userId, drafts);
  return drafts;
}

export function clearDraft(userId: string, conversationId: string): Drafts {
  return saveDraft(userId, conversationId, { text: '' });
}

/** Signing out: drafts are this person's, not the device's. */
export function forgetDrafts(userId: string) {
  // Nothing stored is read back in after this.
  opening.set(userId, Promise.resolve({}));
  write(userId, {});
  try {
    localStorage.removeItem(KEY + userId);
  } catch {
    // Blocked storage has nothing to remove.
  }
}

/**
 * Drops the drafts `keep` says no to (their chat or family is gone) and
 * returns the rest.
 */
export function pruneDrafts(
  userId: string,
  keep: (conversationId: string, draft: Draft) => boolean
): Drafts {
  const drafts = loadDrafts(userId);
  const gone = Object.keys(drafts).filter((id) => !keep(id, drafts[id]!));
  if (gone.length === 0) return drafts;
  gone.forEach((id) => delete drafts[id]);
  write(userId, drafts);
  return drafts;
}

/** The reply target was deleted, or hidden for me, in the loaded chat. */
export function replyTargetGone(
  replyToId: string,
  loaded: ChatMessage[],
  hiddenIds: ReadonlySet<string> = new Set()
): boolean {
  if (hiddenIds.has(replyToId)) return true;
  return Boolean(loaded.find((message) => message.id === replyToId)?.deletedAt);
}

/**
 * The message a draft replies to, as it is now (D228): the loaded copy, else
 * the server's. `null` when it's gone (deleted, hidden, not found): drop the
 * reply. `'unknown'` when it couldn't be checked (offline): keep the id but
 * show no bar, since there's no quote to show without the message.
 */
export async function resolveDraftReply(
  replyToId: string,
  conversationId: string,
  loaded: ChatMessage[],
  fetchMessage: (id: string) => Promise<ChatMessage>
): Promise<ChatMessage | null | 'unknown'> {
  const found = loaded.find((message) => message.id === replyToId);
  if (found) return found.deletedAt ? null : found;
  try {
    const message = await fetchMessage(replyToId);
    return message.deletedAt || message.conversationId !== conversationId
      ? null
      : message;
  } catch (err) {
    const status = (err as { status?: number } | null)?.status;
    return status === 404 || status === 403 ? null : 'unknown';
  }
}

/**
 * The chat list's "Draft: …" text for a chat (D228): one line, and nothing
 * for the chat that's open (you can see the box there).
 */
export function draftPreview(
  drafts: Drafts,
  conversationId: string,
  activeId: string | null
): string | null {
  if (conversationId === activeId) return null;
  const text = drafts[conversationId]?.text.replace(/\s+/g, ' ').trim();
  return text ? text : null;
}
